import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { expenses } from '../services/api';
import { Plus, Trash2, X, Tag, Pencil } from 'lucide-react';

const Expenses: React.FC = () => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [items, setItems] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'cogs' | 'shipping' | 'gateway' | 'marketing' | 'indirect' | 'by-category'>('all');
  const [form, setForm] = useState({ category: '', expense_type: 'indirect', amount: '', date: '', notes: '' });
  const [newCat, setNewCat] = useState({ name: '', expense_type: 'indirect', gst_rate: 0 });
  const [editingExpense, setEditingExpense] = useState<any>(null);
  const [exGst, setExGst] = useState(false);
  const [lastUsedDate, setLastUsedDate] = useState<string | null>(null);

  // Bank import state
  const [showImport, setShowImport] = useState(false);
  const [importStep, setImportStep] = useState<'upload' | 'review'>('upload');
  const [importRows, setImportRows] = useState<any[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importDrag, setImportDrag] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  // Invoice scan state
  const [showScan, setShowScan] = useState(false);
  const [scanStep, setScanStep] = useState<'upload' | 'review'>('upload');
  const [scanFile, setScanFile] = useState<File | null>(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanForm, setScanForm] = useState({ supplier: '', invoice_date: '', invoice_number: '', base_amount: '', gst_amount: '', total_amount: '', category: '', expense_type: 'indirect' });
  const [scanDuplicate, setScanDuplicate] = useState<{ id: number; date: string | null; amount: number; notes: string | null } | null>(null);

  const lastDayOfMonth = (y: number, m: number) => new Date(y, m, 0).toISOString().split('T')[0];

  const blankForm = () => ({ category: '', expense_type: 'indirect', amount: '', date: lastUsedDate || lastDayOfMonth(year, month), notes: '' });

  const openAddModal = () => {
    setForm(blankForm());
    setShowNotes(false);
    setEditingExpense(null);
    setShowAdd(true);
  };

  const closeModal = () => {
    setShowAdd(false);
    setEditingExpense(null);
    setShowNotes(false);
    setForm(blankForm());
  };

  const applyPreset = (preset: string) => {
    const t = new Date();
    if (preset === 'this_month') { setMonth(t.getMonth() + 1); setYear(t.getFullYear()); }
    else if (preset === 'last_month') {
      const d = new Date(t.getFullYear(), t.getMonth() - 1, 1);
      setMonth(d.getMonth() + 1); setYear(d.getFullYear());
    }
  };

  const load = () => {
    expenses.getAll(month, year).then(r => setItems(r.data)).catch(() => {});
    expenses.getCategories().then(r => setCategories(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [month, year]);

  const handleAdd = async (keepOpen = false) => {
    if (!form.category || !form.amount) return;
    await expenses.create({ ...form, amount: Number(form.amount) });
    if (form.date) setLastUsedDate(form.date);
    if (keepOpen) {
      setForm(f => ({ ...f, category: '', expense_type: 'indirect', amount: '' }));
      setShowNotes(false);
    } else {
      closeModal();
    }
    load();
  };

  const handleEdit = async () => {
    if (!editingExpense || !form.category || !form.amount) return;
    await expenses.update(editingExpense.id, {
      category: form.category,
      expense_type: form.expense_type,
      amount: Number(form.amount),
      date: form.date,
      notes: form.notes,
    });
    if (form.date) setLastUsedDate(form.date);
    closeModal();
    load();
  };

  const handleAddCategory = async () => {
    if (!newCat.name) return;
    await expenses.createCategory(newCat);
    setNewCat({ name: '', expense_type: 'indirect', gst_rate: 0 });
    load();
  };

  const handleCategorySelect = (name: string) => {
    const cat = categories.find((c: any) => c.name === name);
    setForm({ ...form, category: name, expense_type: cat?.expense_type || 'indirect' });
  };

  const filtered = activeTab === 'all' || activeTab === 'by-category' ? items : items.filter(i => i.expense_type === activeTab);

  const categoryBreakdown = items.reduce((acc: Record<string, { amount: number; type: string }>, i) => {
    if (!acc[i.category]) acc[i.category] = { amount: 0, type: i.expense_type };
    acc[i.category].amount += i.amount;
    return acc;
  }, {});
  const categoryList = Object.entries(categoryBreakdown).sort((a, b) => b[1].amount - a[1].amount);
  const totalByType = (type: string) => items.filter(i => i.expense_type === type).reduce((s, i) => s + i.amount, 0);
  const total = items.reduce((s, i) => s + i.amount, 0);
  const catsByType = (type: string) => categories.filter((c: any) => c.expense_type === type);

  const openImport = () => { setImportStep('upload'); setImportRows([]); setImportFile(null); setImportError(null); setShowImport(true); };
  const closeImport = () => setShowImport(false);

  const handleImportFile = async (file: File) => {
    setImportFile(file);
    setImportLoading(true);
    setImportError(null);
    try {
      const res = await expenses.importStatement(file);
      const rows = res.data.rows.map((r: any) => ({
        ...r,
        category: r.suggested_category || '',
        expense_type: r.suggested_expense_type || 'indirect',
        included: r.suggested_category != null,
      }));
      setImportRows(rows);
      setImportStep('review');
    } catch (e: any) {
      setImportError(e?.response?.data?.detail || 'Failed to parse file. Make sure it is an HDFC bank statement.');
    } finally {
      setImportLoading(false);
    }
  };

  const splitGst = (i: number, rate = 18) => {
    setImportRows(prev => prev.map((r, j) => {
      if (j !== i) return r;
      const gstAmount = Math.round(r.amount * rate / (100 + rate));
      return { ...r, split_gst: true, gst_amount: gstAmount, gst_rate: rate, gst_category: '', gst_expense_type: 'indirect', gst_included: true };
    }));
  };

  const unsplitGst = (i: number) => {
    setImportRows(prev => prev.map((r, j) => j === i ? { ...r, split_gst: false } : r));
  };

  const openScan = () => {
    setScanStep('upload'); setScanFile(null); setScanError(null); setScanDuplicate(null);
    setScanForm({ supplier: '', invoice_date: '', invoice_number: '', base_amount: '', gst_amount: '', total_amount: '', category: '', expense_type: 'indirect' });
    setShowScan(true);
  };
  const closeScan = () => setShowScan(false);

  const handleScanFile = async (file: File) => {
    setScanFile(file); setScanLoading(true); setScanError(null); setScanDuplicate(null);
    try {
      const res = await expenses.scanInvoice(file);
      const d = res.data;
      setScanDuplicate(d.duplicate || null);
      setScanForm({
        supplier: d.supplier || '',
        invoice_date: d.invoice_date || new Date().toISOString().split('T')[0],
        invoice_number: d.invoice_number || '',
        base_amount: d.base_amount != null ? String(d.base_amount) : '',
        gst_amount: d.gst_amount != null ? String(d.gst_amount) : '',
        total_amount: d.total_amount != null ? String(d.total_amount) : '',
        category: '',
        expense_type: 'indirect',
      });
      setScanStep('review');
    } catch (e: any) {
      setScanError(e?.response?.data?.detail || 'Failed to scan invoice.');
    } finally {
      setScanLoading(false);
    }
  };

  const handleScanSave = async () => {
    if (!scanFile) return;
    await expenses.saveInvoice(scanFile, {
      supplier: scanForm.supplier,
      invoice_date: scanForm.invoice_date,
      base_amount: Number(scanForm.base_amount) || 0,
      gst_amount: Number(scanForm.gst_amount) || 0,
      total_amount: Number(scanForm.total_amount) || 0,
      category: scanForm.category,
      expense_type: scanForm.expense_type,
      invoice_number: scanForm.invoice_number,
    });
    if (scanForm.invoice_date) setLastUsedDate(scanForm.invoice_date);
    closeScan();
    load();
  };

  const handleImportBulk = async () => {
    const toImport: any[] = [];
    for (const r of importRows) {
      if (r.split_gst) {
        const baseAmount = r.amount - r.gst_amount;
        if (r.included && r.category && baseAmount > 0)
          toImport.push({ date: r.date, narration: r.narration, amount: baseAmount, category: r.category, expense_type: r.expense_type });
        if (r.gst_included && r.gst_category && r.gst_amount > 0)
          toImport.push({ date: r.date, narration: r.narration + ' (GST)', amount: r.gst_amount, category: r.gst_category, expense_type: r.gst_expense_type });
      } else {
        if (r.included && r.category)
          toImport.push({ date: r.date, narration: r.narration, amount: r.amount, category: r.category, expense_type: r.expense_type });
      }
    }
    await expenses.importBulk(toImport);
    closeImport();
    load();
  };

  const importCount = importRows.reduce((count, r) => {
    if (r.split_gst) {
      return count
        + (r.included && r.category && (r.amount - r.gst_amount) > 0 ? 1 : 0)
        + (r.gst_included && r.gst_category && r.gst_amount > 0 ? 1 : 0);
    }
    return count + (r.included && r.category ? 1 : 0);
  }, 0);

  const importSummary = importRows.reduce((acc: Record<string, { amount: number; type: string }>, r) => {
    const addToSummary = (type: string, amount: number) => {
      if (!acc[type]) acc[type] = { amount: 0, type };
      acc[type].amount += amount;
    };
    if (r.split_gst) {
      if (r.included && r.category) addToSummary(r.expense_type, r.amount - r.gst_amount);
      if (r.gst_included && r.gst_category) addToSummary(r.gst_expense_type, r.gst_amount);
    } else {
      if (r.included && r.category) addToSummary(r.expense_type, r.amount);
    }
    return acc;
  }, {});

  const EXPENSE_TYPES = [
    { value: 'cogs',      label: 'COGS',     color: 'var(--accent-amber)',  bg: 'var(--accent-amber-soft)' },
    { value: 'shipping',  label: 'Shipping',  color: 'var(--accent-green)',  bg: 'rgba(45,212,160,0.12)' },
    { value: 'gateway',   label: 'Gateway',   color: 'var(--accent-purple)', bg: 'var(--accent-purple-soft)' },
    { value: 'marketing', label: 'Marketing', color: '#f97316',              bg: 'rgba(249,115,22,0.12)' },
    { value: 'indirect',  label: 'Fixed',     color: 'var(--accent-blue)',   bg: 'var(--accent-blue-soft)' },
  ] as const;

  const typeStyle = (type: string) => {
    const t = EXPENSE_TYPES.find(x => x.value === type);
    return t ? { color: t.color, background: t.bg } : { color: 'var(--accent-blue)', background: 'var(--accent-blue-soft)' };
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '11px 14px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' as const };
  const labelStyle: React.CSSProperties = { fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' as const, letterSpacing: 0.8, display: 'block', marginBottom: 6, fontWeight: 600 };

  const selectedType = EXPENSE_TYPES.find(x => x.value === form.expense_type);

  return (
    <Layout title="Expenses" subtitle={`${items.length} entries`}>
      <div className="animate-in" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {[['this_month', 'This Month'], ['last_month', 'Last Month']].map(([key, label]) => (
            <button key={key} onClick={() => applyPreset(key)} style={{ padding: '7px 13px', borderRadius: 'var(--radius-sm)', fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>{label}</button>
          ))}
          <select value={month} onChange={e => setMonth(Number(e.target.value))} style={{ ...inputStyle, width: 130 }}>
            {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <input type="number" value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...inputStyle, width: 90 }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={openScan} style={{ padding: '9px 16px', background: 'rgba(157,122,255,0.12)', border: '1px solid rgba(157,122,255,0.2)', borderRadius: 'var(--radius-sm)', color: 'var(--accent-purple)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>📄 Scan Invoice</button>
          <button onClick={openImport} style={{ padding: '9px 16px', background: 'rgba(45,212,160,0.12)', border: '1px solid rgba(45,212,160,0.2)', borderRadius: 'var(--radius-sm)', color: '#2dd4a0', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>↑ Import Statement</button>
          <button onClick={() => setShowCategories(true)} style={{ padding: '9px 16px', background: 'var(--accent-purple-soft)', border: '1px solid rgba(157,122,255,0.15)', borderRadius: 'var(--radius-sm)', color: 'var(--accent-purple)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}><Tag size={14} /> Categories</button>
          <button onClick={openAddModal} style={{ padding: '9px 18px', background: 'linear-gradient(135deg, var(--accent-blue), var(--accent-purple))', border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}><Plus size={15} /> Add Expense</button>
        </div>
      </div>

      <div className="animate-in-1" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 16 }}>
        {EXPENSE_TYPES.map(({ value, label, color }) => (
          <div key={value} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '14px 16px' }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, fontWeight: 600 }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color, letterSpacing: -0.5 }}>₹{totalByType(value).toLocaleString('en-IN')}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        {([['all', 'All'], ['cogs', 'COGS'], ['shipping', 'Shipping'], ['gateway', 'Gateway'], ['marketing', 'Marketing'], ['indirect', 'Fixed'], ['by-category', 'By Category']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setActiveTab(k)} style={{ padding: '8px 14px', borderRadius: 'var(--radius-sm)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', background: activeTab === k ? 'var(--accent-blue-soft)' : 'var(--bg-card)', border: `1px solid ${activeTab === k ? 'rgba(79,125,249,0.2)' : 'var(--border)'}`, color: activeTab === k ? 'var(--accent-blue)' : 'var(--text-secondary)' }}>{l}</button>
        ))}
      </div>

      {activeTab === 'by-category' && (
        <div className="animate-in-2" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 0.8 }}>Spend by Category</span>
            <button
              onClick={() => setExGst(v => !v)}
              style={{ padding: '5px 12px', borderRadius: 6, border: `1px solid ${exGst ? 'rgba(249,115,22,0.3)' : 'var(--border)'}`, background: exGst ? 'rgba(249,115,22,0.1)' : 'var(--bg-elevated)', color: exGst ? '#f97316' : 'var(--text-muted)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
              {exGst ? '÷ Showing Ex-GST' : '÷ GST'}
            </button>
          </div>
          {categoryList.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>No expenses in this period.</div>}
          {categoryList.map(([cat, { amount, type }]) => {
            const catConfig = categories.find((c: any) => c.name === cat);
            const gstRate = catConfig?.gst_rate || 0;
            const displayAmount = exGst && gstRate > 0 ? Math.round(amount / (1 + gstRate / 100)) : amount;
            return (
              <div key={cat} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 10.5, padding: '3px 8px', borderRadius: 20, fontWeight: 600, textTransform: 'uppercase' as const, ...typeStyle(type) }}>{EXPENSE_TYPES.find(x => x.value === type)?.label ?? type}</span>
                  <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500, textTransform: 'capitalize' as const }}>{cat}</span>
                  {exGst && gstRate > 0 && <span style={{ fontSize: 10, color: '#f97316', background: 'rgba(249,115,22,0.1)', padding: '1px 6px', borderRadius: 10, fontWeight: 600 }}>{gstRate}% GST removed</span>}
                  {exGst && gstRate === 0 && <span style={{ fontSize: 10, color: 'var(--text-muted)', background: 'var(--bg-elevated)', padding: '1px 6px', borderRadius: 10 }}>no GST set</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 120, height: 4, background: 'var(--bg-elevated)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(100, (displayAmount / total) * 100)}%`, background: type === 'cogs' ? 'var(--accent-amber)' : 'var(--accent-blue)', borderRadius: 4 }} />
                  </div>
                  <div style={{ textAlign: 'right' as const, minWidth: 110 }}>
                    <div style={{ fontSize: 13, color: 'var(--accent-amber)', fontWeight: 600 }}>₹{displayAmount.toLocaleString('en-IN')}</div>
                    {exGst && gstRate > 0 && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>was ₹{amount.toLocaleString('en-IN')}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="animate-in-2" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', display: activeTab === 'by-category' ? 'none' : 'block' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '1px solid var(--border)' }}>
            {['Date', 'Type', 'Category', 'Amount', 'Notes', ''].map(h => <th key={h} style={{ padding: '12px 18px', textAlign: 'left', fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {filtered.map((item: any) => (
              <tr key={item.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '12px 18px', fontSize: 13, color: 'var(--text-muted)' }}>{new Date(item.date).toLocaleDateString('en-IN')}</td>
                <td style={{ padding: '12px 18px' }}><span style={{ fontSize: 10.5, padding: '3px 8px', borderRadius: 20, fontWeight: 600, textTransform: 'uppercase', ...typeStyle(item.expense_type) }}>{EXPENSE_TYPES.find(x => x.value === item.expense_type)?.label ?? item.expense_type}</span></td>
                <td style={{ padding: '12px 18px', fontSize: 13, color: 'var(--text-primary)', fontWeight: 500, textTransform: 'capitalize' }}>{item.category}</td>
                <td style={{ padding: '12px 18px', fontSize: 13, color: 'var(--accent-amber)', fontWeight: 600 }}>₹{item.amount.toLocaleString('en-IN')}</td>
                <td style={{ padding: '12px 18px', fontSize: 13, color: 'var(--text-muted)' }}>{item.notes || '—'}</td>
                <td style={{ padding: '12px 18px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => {
                        setEditingExpense(item);
                        setForm({
                          category: item.category,
                          expense_type: item.expense_type,
                          amount: String(item.amount),
                          date: new Date(item.date).toISOString().split('T')[0],
                          notes: item.notes || '',
                        });
                        setShowNotes(!!item.notes);
                        setShowAdd(true);
                      }}
                      style={{ background: 'var(--accent-blue-soft)', border: '1px solid rgba(79,125,249,0.12)', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', display: 'flex' }}
                    >
                      <Pencil size={13} color="var(--accent-blue)" />
                    </button>
                    <button onClick={() => { expenses.delete(item.id).then(load); }} style={{ background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.12)', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', display: 'flex' }}><Trash2 size={13} color="var(--accent-red)" /></button>
                    {item.invoice_url && (
                      <a href={item.invoice_url} target="_blank" rel="noreferrer" title="View invoice" style={{ background: 'rgba(157,122,255,0.1)', border: '1px solid rgba(157,122,255,0.15)', borderRadius: 6, padding: '5px 8px', display: 'flex', alignItems: 'center', textDecoration: 'none', fontSize: 12 }}>📄</a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>No expenses recorded.</div>}
      </div>

      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="animate-in" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 32, width: 440, boxShadow: 'var(--shadow-elevated)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{editingExpense ? 'Edit Expense' : 'Add Expense'}</div>
              <button onClick={closeModal} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><X size={15} color="var(--text-muted)" /></button>
            </div>

            {/* Category — type auto-set */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>Category</label>
                {form.category && selectedType && (
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 700, textTransform: 'uppercase', color: selectedType.color, background: selectedType.bg }}>
                    {selectedType.label}
                  </span>
                )}
              </div>
              <select value={form.category} onChange={e => handleCategorySelect(e.target.value)} style={inputStyle}>
                <option value="">Select category...</option>
                {EXPENSE_TYPES.map(({ value, label }) => {
                  const cats = catsByType(value);
                  if (cats.length === 0) return null;
                  return (
                    <optgroup key={value} label={label}>
                      {cats.map((c: any) => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </optgroup>
                  );
                })}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div><label style={labelStyle}>Amount (₹)</label><input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} style={inputStyle} placeholder="0" /></div>
              <div><label style={labelStyle}>Date</label><input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} style={inputStyle} /></div>
            </div>

            {/* Notes — hidden by default */}
            <div style={{ marginBottom: 20 }}>
              {showNotes ? (
                <>
                  <label style={labelStyle}>Notes</label>
                  <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={inputStyle} placeholder="Optional" autoFocus />
                </>
              ) : (
                <button onClick={() => setShowNotes(true)} style={{ background: 'none', border: 'none', color: 'var(--accent-blue)', fontSize: 12, cursor: 'pointer', padding: 0, fontWeight: 600 }}>+ add note</button>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
              {!editingExpense && (
                <button onClick={() => handleAdd(true)} disabled={!form.category || !form.amount} style={{ width: '100%', padding: 12, background: (form.category && form.amount) ? 'linear-gradient(135deg, var(--accent-blue), var(--accent-purple))' : 'var(--bg-elevated)', border: 'none', borderRadius: 'var(--radius-sm)', color: (form.category && form.amount) ? '#fff' : 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: (form.category && form.amount) ? 'pointer' : 'default' }}>Save & Add Another</button>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={editingExpense ? handleEdit : () => handleAdd(false)} disabled={!form.category || !form.amount} style={{ flex: 1, padding: 12, background: (form.category && form.amount) ? 'var(--bg-elevated)' : 'var(--bg-elevated)', border: `1px solid ${(form.category && form.amount) ? 'var(--border)' : 'var(--border)'}`, borderRadius: 'var(--radius-sm)', color: (form.category && form.amount) ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: (form.category && form.amount) ? 'pointer' : 'default' }}>{editingExpense ? 'Save Changes' : 'Add & Close'}</button>
                <button onClick={closeModal} style={{ padding: '12px 20px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCategories && <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={e => { if (e.target === e.currentTarget) setShowCategories(false); }}>
        <div className="animate-in" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 28, width: 460, boxShadow: 'var(--shadow-elevated)', maxHeight: '80vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}><div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Expense Categories</div><button onClick={() => setShowCategories(false)} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><X size={15} color="var(--text-muted)" /></button></div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>New Category</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input value={newCat.name} onChange={e => setNewCat({ ...newCat, name: e.target.value })} style={{ ...inputStyle, flex: 1 }} placeholder="Category name..." />
              <select value={newCat.expense_type} onChange={e => setNewCat({ ...newCat, expense_type: e.target.value })} style={{ ...inputStyle, width: 130 }}>{EXPENSE_TYPES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}</select>
              <select value={newCat.gst_rate} onChange={e => setNewCat({ ...newCat, gst_rate: Number(e.target.value) })} style={{ ...inputStyle, width: 90 }}>
                {[0, 5, 12, 18, 28].map(r => <option key={r} value={r}>{r === 0 ? 'No GST' : `${r}% GST`}</option>)}
              </select>
            </div>
            <button onClick={handleAddCategory} disabled={!newCat.name} style={{ padding: '10px 18px', width: '100%', background: newCat.name ? 'linear-gradient(135deg, var(--accent-purple), #7c3aed)' : 'var(--bg-elevated)', border: 'none', borderRadius: 'var(--radius-sm)', color: newCat.name ? '#fff' : 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: newCat.name ? 'pointer' : 'default' }}>Add Category</button>
          </div>
          {EXPENSE_TYPES.map(({ value, label, color, bg }) => (
            <div key={value}>
              <label style={{ ...labelStyle, marginTop: 16 }}>{label} — {catsByType(value).length}</label>
              {catsByType(value).map((c: any) => (
                <div key={c.id} style={{ background: bg, borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color }}>{c.name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <select
                      value={c.gst_rate || 0}
                      onChange={async e => { await expenses.updateCategory(c.id, { gst_rate: Number(e.target.value) }); load(); }}
                      style={{ padding: '3px 6px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-primary)', fontSize: 11, cursor: 'pointer' }}
                    >
                      {[0, 5, 12, 18, 28].map(r => <option key={r} value={r}>{r === 0 ? 'No GST' : `${r}%`}</option>)}
                    </select>
                    <button
                      title={c.exclude_from_pl ? 'Hidden from P&L — click to include' : 'Shown in P&L — click to hide'}
                      onClick={async () => { await expenses.updateCategory(c.id, { exclude_from_pl: !c.exclude_from_pl }); load(); }}
                      style={{ padding: '3px 7px', fontSize: 10, fontWeight: 700, borderRadius: 4, border: `1px solid ${c.exclude_from_pl ? 'rgba(239,90,111,0.3)' : 'var(--border)'}`, background: c.exclude_from_pl ? 'rgba(239,90,111,0.1)' : 'var(--bg-elevated)', color: c.exclude_from_pl ? '#ef5a6f' : 'var(--text-muted)', cursor: 'pointer' }}>
                      {c.exclude_from_pl ? 'P&L off' : 'P&L on'}
                    </button>
                    <button onClick={async () => { await expenses.deleteCategory(c.id); load(); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><Trash2 size={12} color="var(--accent-red)" /></button>
                  </div>
                </div>
              ))}
              {catsByType(value).length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>No {label.toLowerCase()} categories</div>}
            </div>
          ))}
        </div>
      </div>}

      {showImport && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={e => { if (e.target === e.currentTarget) closeImport(); }}>
          <div className="animate-in" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 28, width: importStep === 'review' ? 780 : 460, maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-elevated)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>{importStep === 'upload' ? 'Import Bank Statement' : 'Review Transactions'}</div>
              <button onClick={closeImport} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><X size={15} color="var(--text-muted)" /></button>
            </div>

            {importStep === 'upload' && (
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>HDFC Bank · XLS / XLSX format</div>
                <label
                  onDragOver={e => { e.preventDefault(); setImportDrag(true); }}
                  onDragLeave={() => setImportDrag(false)}
                  onDrop={e => { e.preventDefault(); setImportDrag(false); const f = e.dataTransfer.files[0]; if (f) handleImportFile(f); }}
                  style={{ display: 'block', border: `2px dashed ${importDrag ? '#2dd4a0' : 'var(--border)'}`, borderRadius: 10, padding: '40px 24px', textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.15s' }}
                >
                  <input type="file" accept=".xls,.xlsx" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) handleImportFile(f); }} />
                  <div style={{ fontSize: 32, marginBottom: 10 }}>📄</div>
                  {importLoading ? (
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Parsing & analysing...</div>
                  ) : importFile ? (
                    <div style={{ fontSize: 13, color: '#2dd4a0', fontWeight: 600 }}>{importFile.name}</div>
                  ) : (
                    <>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600, marginBottom: 4 }}>Drop your XLS file here</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>or click to browse</div>
                    </>
                  )}
                </label>
                {importError && (
                  <div style={{ marginTop: 14, padding: '10px 14px', background: 'rgba(239,90,111,0.1)', border: '1px solid rgba(239,90,111,0.2)', borderRadius: 8, fontSize: 12, color: '#ef5a6f' }}>
                    {importError}
                  </div>
                )}
              </div>
            )}

            {importStep === 'review' && (
              <div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginRight: 4 }}>{importRows.length} transactions</span>
                  {EXPENSE_TYPES.map(({ value, label, color, bg }) => {
                    const s = importSummary[value];
                    if (!s) return null;
                    return <span key={value} style={{ background: bg, color, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>{label} ₹{s.amount.toLocaleString('en-IN')}</span>;
                  })}
                  {importRows.filter(r => !r.suggested_category).length > 0 && (
                    <span style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20 }}>{importRows.filter(r => !r.suggested_category).length} unassigned</span>
                  )}
                </div>

                <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '20px 80px 1fr 120px 185px 50px 40px', padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' as const, letterSpacing: 0.8 }}>
                    <div></div><div>Date</div><div>Narration</div><div>Amount</div><div>Category</div><div style={{ textAlign: 'center' as const }}>÷GST</div><div style={{ textAlign: 'center' as const }}>✓</div>
                  </div>
                  {importRows.map((row, i) => {
                    const dotColor = row.match_method === 'ai' ? '#2dd4a0' : row.match_method === 'keyword' ? '#9d7aff' : '#ef5a6f';
                    const GST_RATES = [5, 12, 18, 28];
                    const catSelect = (value: string, onChange: (val: string, type: string) => void) => (
                      <select value={value} onChange={e => { const cat = categories.find((c: any) => c.name === e.target.value); onChange(e.target.value, cat?.expense_type || 'indirect'); }} style={{ ...inputStyle, fontSize: 11, padding: '5px 8px' }}>
                        <option value="">Unassigned</option>
                        {EXPENSE_TYPES.map(({ value: v, label }) => { const cats = catsByType(v); if (!cats.length) return null; return <optgroup key={v} label={label}>{cats.map((c: any) => <option key={c.id} value={c.name}>{c.name}</option>)}</optgroup>; })}
                      </select>
                    );

                    if (row.split_gst) {
                      const baseAmount = row.amount - row.gst_amount;
                      return (
                        <div key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                          {/* Base row */}
                          <div style={{ display: 'grid', gridTemplateColumns: '20px 80px 1fr 120px 185px 50px 40px', alignItems: 'center', padding: '8px 12px', fontSize: 12 }}>
                            <div style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor }} />
                            <div style={{ color: 'var(--text-muted)' }}>{row.date}</div>
                            <div style={{ color: 'var(--text-primary)', paddingRight: 8, wordBreak: 'break-word' as const, fontSize: 11 }}>{row.narration}</div>
                            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 2 }}>
                              <span style={{ color: '#f59e0b', fontWeight: 700, fontSize: 12 }}>₹{baseAmount.toLocaleString('en-IN')}</span>
                              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>base (excl. GST)</span>
                            </div>
                            {catSelect(row.category, (val, type) => setImportRows(prev => prev.map((r, j) => j === i ? { ...r, category: val, expense_type: type, included: !!val } : r)))}
                            <div style={{ textAlign: 'center' as const }}>
                              <button onClick={() => unsplitGst(i)} title="Remove split" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#f97316' }}>×</button>
                            </div>
                            <div style={{ textAlign: 'center' as const }}>
                              <input type="checkbox" checked={row.included && !!row.category} disabled={!row.category} onChange={e => setImportRows(prev => prev.map((r, j) => j === i ? { ...r, included: e.target.checked } : r))} />
                            </div>
                          </div>
                          {/* GST sub-row */}
                          <div style={{ display: 'grid', gridTemplateColumns: '20px 80px 1fr 120px 185px 50px 40px', alignItems: 'center', padding: '6px 12px 8px 12px', fontSize: 11, background: 'rgba(249,115,22,0.05)', borderTop: '1px dashed rgba(249,115,22,0.2)' }}>
                            <div />
                            <div />
                            <div style={{ color: '#f97316', fontSize: 10, fontWeight: 600 }}>↳ GST portion</div>
                            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 3 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>₹</span>
                                <input
                                  type="number"
                                  value={row.gst_amount}
                                  onChange={e => setImportRows(prev => prev.map((r, j) => j === i ? { ...r, gst_amount: Math.max(0, Math.min(Number(e.target.value), r.amount - 1)) } : r))}
                                  style={{ width: 72, padding: '3px 6px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 4, color: '#f97316', fontSize: 11, fontWeight: 700 }}
                                />
                              </div>
                              <div style={{ display: 'flex', gap: 3 }}>
                                {GST_RATES.map(rate => (
                                  <button key={rate} onClick={() => { const gst = Math.round(row.amount * rate / (100 + rate)); setImportRows(prev => prev.map((r, j) => j === i ? { ...r, gst_amount: gst, gst_rate: rate } : r)); }} style={{ padding: '1px 5px', fontSize: 9, fontWeight: 700, borderRadius: 4, border: `1px solid ${row.gst_rate === rate ? '#f97316' : 'var(--border)'}`, background: row.gst_rate === rate ? 'rgba(249,115,22,0.15)' : 'var(--bg-elevated)', color: row.gst_rate === rate ? '#f97316' : 'var(--text-muted)', cursor: 'pointer' }}>{rate}%</button>
                                ))}
                              </div>
                            </div>
                            {catSelect(row.gst_category, (val, type) => setImportRows(prev => prev.map((r, j) => j === i ? { ...r, gst_category: val, gst_expense_type: type, gst_included: !!val } : r)))}
                            <div />
                            <div style={{ textAlign: 'center' as const }}>
                              <input type="checkbox" checked={row.gst_included && !!row.gst_category} disabled={!row.gst_category} onChange={e => setImportRows(prev => prev.map((r, j) => j === i ? { ...r, gst_included: e.target.checked } : r))} />
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '20px 80px 1fr 120px 185px 50px 40px', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12, background: !row.suggested_category ? 'rgba(239,90,111,0.03)' : 'transparent' }}>
                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor }} />
                        <div style={{ color: 'var(--text-muted)' }}>{row.date}</div>
                        <div style={{ color: 'var(--text-primary)', paddingRight: 8, wordBreak: 'break-word' as const, fontSize: 11 }}>{row.narration}</div>
                        <div style={{ color: '#f59e0b', fontWeight: 700 }}>₹{row.amount.toLocaleString('en-IN')}</div>
                        {catSelect(row.category, (val, type) => setImportRows(prev => prev.map((r, j) => j === i ? { ...r, category: val, expense_type: type, included: !!val } : r)))}
                        <div style={{ textAlign: 'center' as const }}>
                          <button onClick={() => splitGst(i)} title="Split GST" style={{ fontSize: 10, fontWeight: 700, padding: '3px 6px', borderRadius: 4, border: '1px solid rgba(249,115,22,0.3)', background: 'rgba(249,115,22,0.08)', color: '#f97316', cursor: 'pointer' }}>÷GST</button>
                        </div>
                        <div style={{ textAlign: 'center' as const }}>
                          <input type="checkbox" checked={row.included && !!row.category} disabled={!row.category} onChange={e => setImportRows(prev => prev.map((r, j) => j === i ? { ...r, included: e.target.checked } : r))} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Credits excluded · <span style={{ color: '#2dd4a0' }}>● AI</span> · <span style={{ color: '#9d7aff' }}>● keyword</span> · <span style={{ color: '#ef5a6f' }}>● unmatched</span></div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setImportStep('upload')} style={{ padding: '9px 16px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer' }}>← Back</button>
                    <button
                      onClick={handleImportBulk}
                      disabled={importCount === 0}
                      style={{ padding: '9px 18px', background: importCount > 0 ? 'linear-gradient(135deg,#2dd4a0,#059669)' : 'var(--bg-elevated)', border: 'none', borderRadius: 'var(--radius-sm)', color: importCount > 0 ? '#fff' : 'var(--text-muted)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                      Import {importCount} Expenses →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showScan && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={e => { if (e.target === e.currentTarget) closeScan(); }}>
          <div className="animate-in" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 28, width: scanStep === 'review' ? 500 : 420, maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-elevated)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>{scanStep === 'upload' ? 'Scan Invoice' : 'Review Extracted Data'}</div>
              <button onClick={closeScan} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><X size={15} color="var(--text-muted)" /></button>
            </div>

            {scanStep === 'upload' && (
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>Supplier invoice · PDF format</div>
                <label style={{ display: 'block', border: '2px dashed var(--border)', borderRadius: 10, padding: '40px 24px', textAlign: 'center', cursor: 'pointer' }}>
                  <input type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) handleScanFile(f); }} />
                  <div style={{ fontSize: 32, marginBottom: 10 }}>📄</div>
                  {scanLoading ? (
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Scanning invoice...</div>
                  ) : scanFile ? (
                    <div style={{ fontSize: 13, color: 'var(--accent-purple)', fontWeight: 600 }}>{scanFile.name}</div>
                  ) : (
                    <>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600, marginBottom: 4 }}>Drop PDF here or click to browse</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>First page will be scanned by AI</div>
                    </>
                  )}
                </label>
                {scanError && <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(239,90,111,0.1)', border: '1px solid rgba(239,90,111,0.2)', borderRadius: 8, fontSize: 12, color: '#ef5a6f' }}>{scanError}</div>}
              </div>
            )}

            {scanStep === 'review' && (
              <div>
                {scanDuplicate && (
                  <div style={{ marginBottom: 14, padding: '11px 14px', background: 'var(--accent-amber-soft)', border: '1px solid rgba(245,166,35,0.15)', borderRadius: 'var(--radius-sm)', fontSize: 12.5, color: 'var(--accent-amber)' }}>
                    ⚠️ Possible duplicate — an expense for this supplier/invoice number was already saved on {scanDuplicate.date ? new Date(scanDuplicate.date).toLocaleDateString('en-IN') : 'an earlier date'} for ₹{scanDuplicate.amount.toLocaleString('en-IN')}.
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Supplier Name</label>
                    <input value={scanForm.supplier} onChange={e => setScanForm(f => ({ ...f, supplier: e.target.value }))} style={inputStyle} placeholder="Supplier name..." />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={labelStyle}>Invoice Date</label>
                      <input type="date" value={scanForm.invoice_date} onChange={e => setScanForm(f => ({ ...f, invoice_date: e.target.value }))} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Invoice Number</label>
                      <input value={scanForm.invoice_number} onChange={e => setScanForm(f => ({ ...f, invoice_number: e.target.value }))} style={inputStyle} placeholder="Optional" />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    <div>
                      <label style={labelStyle}>Base (₹)</label>
                      <input type="number" value={scanForm.base_amount} onChange={e => setScanForm(f => ({ ...f, base_amount: e.target.value }))} style={inputStyle} placeholder="0" />
                    </div>
                    <div>
                      <label style={labelStyle}>GST (₹)</label>
                      <input type="number" value={scanForm.gst_amount} onChange={e => setScanForm(f => ({ ...f, gst_amount: e.target.value }))} style={inputStyle} placeholder="0" />
                    </div>
                    <div>
                      <label style={labelStyle}>Total (₹)</label>
                      <input type="number" value={scanForm.total_amount} onChange={e => setScanForm(f => ({ ...f, total_amount: e.target.value }))} style={inputStyle} placeholder="0" />
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Expense Category</label>
                    <select value={scanForm.category} onChange={e => { const cat = categories.find((c: any) => c.name === e.target.value); setScanForm(f => ({ ...f, category: e.target.value, expense_type: cat?.expense_type || 'indirect' })); }} style={inputStyle}>
                      <option value="">Select category...</option>
                      {EXPENSE_TYPES.map(({ value: v, label }) => {
                        const cats = catsByType(v);
                        if (!cats.length) return null;
                        return <optgroup key={v} label={label}>{cats.map((c: any) => <option key={c.id} value={c.name}>{c.name}</option>)}</optgroup>;
                      })}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                  <button onClick={handleScanSave} disabled={!scanForm.supplier || !scanForm.base_amount || !scanForm.category} style={{ flex: 1, padding: 12, background: (scanForm.supplier && scanForm.base_amount && scanForm.category) ? 'linear-gradient(135deg, var(--accent-purple), #7c3aed)' : 'var(--bg-elevated)', border: 'none', borderRadius: 'var(--radius-sm)', color: (scanForm.supplier && scanForm.base_amount && scanForm.category) ? '#fff' : 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Save Expense</button>
                  <button onClick={() => setScanStep('upload')} style={{ padding: '12px 16px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>← Back</button>
                  <button onClick={closeScan} style={{ padding: '12px 16px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Expenses;
